import { useState } from "react";
import api from "../utils/api";

// GS1 Digital Link URI scheme: /01/{gtin14}?pg={pdp|brand}&ch={retailer}
const SIDE_TO_PG = { front: "pdp", back: "brand" };
const CHANNEL_RE = /^[a-z0-9_]{1,11}$/;

function gtinCheckDigitOk(gtin) {
  if (!/^\d{14}$/.test(gtin)) return false;
  const d = gtin.split("").map(Number);
  let sum = 0;
  for (let i = 0; i < 13; i++) sum += d[i] * (i % 2 === 0 ? 3 : 1);
  return (10 - (sum % 10)) % 10 === d[13];
}

const AddQrCodeModal = ({ productId, productName, productUpc, productRetailer, options, onClose, onSaved }) => {
  const [side, setSide] = useState("front");
  const [destUrl, setDestUrl] = useState("");
  const [notes, setNotes] = useState("");
  const hasUpc = !!productUpc;
  const pg = SIDE_TO_PG[side] || "pdp";
  const ch = productRetailer || "";
  const upcDigits = hasUpc ? productUpc.replace(/\D/g, "") : "";
  const gtin = [8, 12, 13, 14].includes(upcDigits.length) ? upcDigits.padStart(14, "0") : null;
  const gtinOk = !!gtin && gtinCheckDigitOk(gtin);
  const chOk = CHANNEL_RE.test(ch);
  const gs1Ready = gtinOk && chOk;

  // Default to GS1 only when the product can actually mint one
  const [qrFormat, setQrFormat] = useState(gs1Ready ? "gs1" : "standard");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post(`/api/products/${productId}/qrcodes`, {
        side, destUrl, notes, qrFormat,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create QR code");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add QR Code to {productName}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              {/* Format toggle */}
              <div className="mb-3">
                <label className="form-label">QR Code Format</label>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn btn-sm flex-fill ${qrFormat === "gs1" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setQrFormat("gs1")}
                    disabled={!gs1Ready}
                  >
                    GS1 Digital Link
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm flex-fill ${qrFormat === "standard" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setQrFormat("standard")}
                  >
                    Standard
                  </button>
                </div>
                {!hasUpc && (
                  <div className="form-text text-warning">GS1 requires a UPC on the product. Add a UPC to enable GS1 format.</div>
                )}
                {hasUpc && !gtinOk && (
                  <div className="form-text text-danger">UPC "{productUpc}" is not a valid GTIN (wrong length or check digit). Fix the product UPC to enable GS1 format.</div>
                )}
                {gtinOk && !chOk && (
                  <div className="form-text text-danger">Retailer "{ch}" is not a valid channel slug (lowercase a-z, 0-9, underscore, max 11 chars).</div>
                )}
              </div>

              {/* GS1 preview */}
              {qrFormat === "gs1" && gs1Ready && (
                <div className="mb-3 p-2 rounded" style={{ backgroundColor: "#f0f7ff", fontSize: "13px" }}>
                  <div className="text-muted mb-1" style={{ fontSize: "11px" }}>QR code will encode (GS1 Digital Link, error correction M):</div>
                  <code>{window.location.origin}/01/{gtin}?pg={pg}&ch={ch}</code>
                  <div className="text-muted mt-1" style={{ fontSize: "11px" }}>
                    pg={pg}: {pg === "pdp" ? "product page (front panel)" : "brand page (back panel, adjacent to UPC)"} &nbsp;|&nbsp; ch={ch}: retail channel
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Side</label>
                <select className="form-select" value={side} onChange={(e) => setSide(e.target.value)}>
                  {qrFormat === "gs1" ? (
                    <>
                      <option value="front">front</option>
                      <option value="back">back</option>
                    </>
                  ) : (
                    options?.sides?.map((s) => (<option key={s} value={s}>{s}</option>))
                  )}
                </select>
                {qrFormat === "gs1" && (
                  <div className="form-text">
                    Front → <code>pg=pdp</code> (product page) &nbsp;|&nbsp; Back → <code>pg=brand</code> (brand page)
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label">Destination URL <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="https://www.walmart.com/ip/..."
                  value={destUrl}
                  onChange={(e) => setDestUrl(e.target.value)}
                  required
                />
                <div className="form-text">Where the consumer will be redirected when they scan.</div>
              </div>
              <div className="mb-3">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  rows="2"
                  placeholder="Optional notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" type="button" onClick={onClose}>Cancel</button>
              <button className="btn btn-success" type="submit" disabled={saving}>
                {saving ? "Creating..." : `Create ${qrFormat === "gs1" ? "GS1" : "Standard"} QR Code`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddQrCodeModal;
